import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gauge, Zap } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Displays a compact hourly credit usage widget:
 *   "12 / 500 · Reset in 42 min"
 * Refreshes on mount and via a `refreshKey` prop (bump to re-fetch).
 */
export default function CreditUsageWidget({ refreshKey = 0, className = "" }) {
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await api.creditsUsageHour();
                if (!cancelled) setUsage(data);
            } catch (e) {
                if (!cancelled) setErr(e.message || "usage unavailable");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);

    if (loading && !usage) {
        return (
            <div
                data-testid="credit-usage-widget"
                className={`inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1.5 text-xs text-text-secondary ${className}`}
            >
                <Gauge className="h-3.5 w-3.5" />
                Loading usage…
            </div>
        );
    }

    if (err || !usage) {
        return (
            <div
                data-testid="credit-usage-widget"
                className={`inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1.5 text-xs text-text-secondary ${className}`}
            >
                <Gauge className="h-3.5 w-3.5" />
                Usage n/a
            </div>
        );
    }

    const pct = Math.min(100, Math.round((usage.consumed_last_hour / Math.max(1, usage.cap)) * 100));
    const resetMin = Math.max(0, Math.round(usage.retry_after / 60));
    const nearCap = pct >= 80;

    return (
        <motion.div
            data-testid="credit-usage-widget"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className={`inline-flex items-center gap-3 rounded-full border px-3 py-1.5 text-xs bg-[var(--color-card)] ${
                nearCap
                    ? "border-[var(--color-alert)]/50 text-[var(--color-alert)]"
                    : "border-[var(--color-border)] text-text-secondary"
            } ${className}`}
        >
            <span className="inline-flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5" />
                <span
                    className="tabular-nums font-medium"
                    data-testid="credit-usage-value"
                >
                    {usage.consumed_last_hour}
                </span>
                <span className="opacity-60">/</span>
                <span
                    className="tabular-nums"
                    data-testid="credit-usage-cap"
                >
                    {usage.cap}
                </span>
                <span className="opacity-60 ml-1">this hour</span>
            </span>
            <span className="hidden sm:inline w-14 h-1 rounded-full bg-[var(--color-panel-ai)] overflow-hidden">
                <span
                    className={`block h-full ${
                        nearCap ? "bg-[var(--color-alert)]" : "gradient-bg-cta"
                    }`}
                    style={{ width: `${pct}%` }}
                />
            </span>
            <span
                data-testid="credit-usage-reset"
                className="hidden md:inline opacity-70"
            >
                Reset in {resetMin} min
            </span>
        </motion.div>
    );
}
